<template>
  <DraggableModalWrapper :is-visible="isVisible">
    <div :id="`${source}_dialpad`" class="flex flex-col">
      <div class="flex justify-end p-1">
        <ThemeImage
          class="size-5 cursor-pointer"
          :icon="imageNames.CROSS_GREY"
          alt="cross_icon"
          @click="closeDialPad"
        />
      </div>

      <Dialer
        :source="`${source}_dialpad`"
        :dial="true"
        @update:value="updatePhoneNumber"
      />

      <div
        :id="`${source}_dialpad_footer`"
        class="flex flex-row items-center justify-between p-2 border_top"
      >
        <div class="flex flex-col">
          <span class="text-[13px] secondary_color">Call via</span>

          <a-select
            v-model:value="callParams.From"
            class="w-[134px]"
            placeholder="select a number"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4 opacity-60"
              />
            </template>

            <a-select-option
              v-for="(number, index) in callViaList"
              :key="`${number.id}_number_${index}`"
              :value="number.phone_number"
              :label="number.phone_number"
            >
              {{ number.phone_number }}
            </a-select-option>
          </a-select>
        </div>

        <a-button
          class="handle_call"
          @click.stop="handleCall"
          @mousedown.stop
          @touchstart.stop
          :disabled="handleButtonDisabled"
        >
          <ThemeImage
            class="size-5"
            :icon="imageNames.DIALER_PHONE_ICON"
            alt="dialer_phone_icon"
          />
        </a-button>
      </div>
    </div>
  </DraggableModalWrapper>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import {
  initializeAndMakeCall,
  toFetchPhoneNumberDetails,
  toGetPhoneNumbers,
  validatePhoneNumber,
} from "@/src/utils/callsUtils.js";
import { message } from "ant-design-vue";
import { validationEntities } from "@/src/constants/callConstants.js";
import store from "@/src/store/index.js";

import sidebarImagePath from "@/src/paths/sidebarImagePath.js";
import Dialer from "@/src/components/telephony_components/dialer/Dialer.vue";
import DraggableModalWrapper from "@/src/components/telephony_components/modules/DraggableModalWrapper.vue";

import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
});

const loading = ref(false);
const callViaList = ref(null);
const phoneNumber = ref("");
const callParams = ref({
  To: null,
  From: null,
});

const isVisible = computed(() => store.getters["calls/showDial"]);

const handleButtonDisabled = computed(() => {
  return loading.value || !phoneNumber.value || !callParams.value.From;
});

const updatePhoneNumber = (value) => {
  try {
    phoneNumber.value = value;
  } catch (error) {
    console.error("An error occurred while updating phone number: ", error);
  }
};

const closeDialPad = async () => {
  try {
    await store.dispatch("calls/setShowDial", false);
  } catch (error) {
    console.error("An error occurred while closing dial pad: ", error);
  }
};

const handleCall = async () => {
  try {
    if (!phoneNumber.value || !callParams.value.From) return;

    const number = phoneNumber.value?.replace(/\s+/g, "");
    const validationResponse = validatePhoneNumber(
      number,
      validationEntities.NORMAL_CALL,
    );

    if (!validationResponse?.valid) {
      message.error(validationResponse?.error);
      return;
    }

    const contactDetails = await toFetchPhoneNumberDetails(number);
    let payload = {
      phoneNumber: number,
      callParams: {
        To: number,
        From: callParams.value.From,
      },
    };

    if (contactDetails) {
      payload = {
        ...payload,
        entityDetails: contactDetails,
        isCampaign: false,
        entityType: contactDetails.type,
        callerId: contactDetails.id,
      };
    }

    await initializeAndMakeCall(payload);
    await closeDialPad();
  } catch (error) {
    console.error("An error occurred while handling call: ", error);
  }
};

const fetchPhoneNumbers = async () => {
  try {
    loading.value = true;

    const filters = { user: true };
    callViaList.value = await toGetPhoneNumbers(filters);

    if (callViaList.value && callViaList.value.length > 0) {
      callParams.value.From = callViaList.value[0].phone_number;
    }
  } catch (error) {
    console.error("An error occurred while fetching phone numbers: ", error);
  } finally {
    loading.value = false;
  }
};

watch(phoneNumber, (newValue) => {
  callParams.value.To = newValue;
});

onMounted(() => {
  fetchPhoneNumbers();
});
</script>

<style scoped>
:deep(.ant-select-selector) {
  background: transparent !important;
  border-color: transparent !important;
  padding: 0 !important;
}

:deep(.vue-tel-input) {
  background: transparent !important;
  border-color: transparent !important;
  padding: 0 !important;
}
</style>
